<template>
  <div class="align-items-center justify-content-center" style="display: flex">
    <div style="width: 100%; max-width: 1200px; min-width: 320px">
      <h2 style="text-align: center; margin-bottom: 12px">API-Schlüssel</h2>

      <Message severity="info" :closable="false" style="margin-bottom: 16px">
        Schlüssel für die öffentliche, ausschließlich lesende Schnittstelle unter
        <code>/api/v1</code>. Der Klartext wird nur einmal beim Anlegen angezeigt und
        kann danach nicht mehr abgerufen werden. Kontingente gelten je Schlüssel und
        werden zum Minuten- bzw. Tagesende zurückgesetzt.
      </Message>

      <h4 v-if="error && error.length > 0" style="color: red">{{ error }}</h4>

      <div style="display: flex; gap: 8px; margin-bottom: 12px">
        <Button
          label="Neuen Schlüssel anlegen"
          icon="pi pi-plus"
          :disabled="busy"
          @click="openCreateDialog"
        />
        <Button
          label="Neu laden"
          icon="pi pi-refresh"
          severity="secondary"
          :disabled="busy"
          @click="loadKeys"
        />
      </div>

      <DataTable
        :value="apiKeys"
        :loading="loading"
        v-model:expandedRows="expandedRows"
        dataKey="id"
        stripedRows
        responsiveLayout="scroll"
        @rowExpand="onRowExpand"
      >
        <Column expander headerStyle="width: 3rem" />
        <Column field="name" header="Name" sortable />
        <Column header="Schlüssel">
          <template #body="slotProps">
            <code>{{ slotProps.data.displayHint }}</code>
          </template>
        </Column>
        <Column field="ownerName" header="Kontakt">
          <template #body="slotProps">
            <div>{{ slotProps.data.ownerName }}</div>
            <div style="font-size: 0.85em; color: #6c757d">
              {{ slotProps.data.ownerMail }}
            </div>
          </template>
        </Column>
        <Column header="Kontingent">
          <template #body="slotProps">
            {{ quotaText(slotProps.data) }}
          </template>
        </Column>
        <Column header="Status">
          <template #body="slotProps">
            <span v-if="isExpired(slotProps.data)" style="color: #b3261e">abgelaufen</span>
            <span v-else-if="slotProps.data.active" style="color: #2e7d32">aktiv</span>
            <span v-else style="color: #b3261e">gesperrt</span>
          </template>
        </Column>
        <Column header="Zuletzt benutzt">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.lastUsedAt) }}
          </template>
        </Column>
        <Column headerStyle="width: 12rem">
          <template #body="slotProps">
            <div style="display: flex; gap: 4px">
              <Button
                icon="pi pi-pencil"
                severity="secondary"
                size="small"
                title="Bearbeiten"
                :disabled="busy"
                @click="openEditDialog(slotProps.data)"
              />
              <Button
                v-if="slotProps.data.active"
                icon="pi pi-lock"
                severity="warning"
                size="small"
                title="Sperren"
                :disabled="busy"
                @click="openBlockDialog(slotProps.data)"
              />
              <Button
                v-else
                icon="pi pi-lock-open"
                severity="success"
                size="small"
                title="Entsperren"
                :disabled="busy"
                @click="unblockKey(slotProps.data)"
              />
              <Button
                icon="pi pi-trash"
                severity="danger"
                size="small"
                title="Löschen"
                :disabled="busy"
                @click="openDeleteDialog(slotProps.data)"
              />
            </div>
          </template>
        </Column>

        <template #expansion="slotProps">
          <div style="padding: 8px 12px">
            <div v-if="loadingUsage[slotProps.data.id]">
              Nutzung wird geladen &nbsp;<i class="pi pi-spin pi-spinner" />
            </div>
            <div
              v-else-if="!usage[slotProps.data.id] || usage[slotProps.data.id].length === 0"
              style="color: #6c757d; font-style: italic"
            >
              Für diesen Schlüssel wurde noch keine Nutzung erfasst.
            </div>
            <div v-else>
              <div style="margin-bottom: 6px">
                <strong>Nutzung der letzten 30 Tage</strong>
                <span style="color: #6c757d">
                  &nbsp;— {{ totalRequests(slotProps.data.id) }} Anfragen,
                  {{ totalRateLimited(slotProps.data.id) }} abgewiesen
                </span>
              </div>
              <table style="width: 100%; border-collapse: collapse">
                <thead>
                  <tr style="text-align: left; border-bottom: 2px solid #dee2e6">
                    <th style="padding: 4px">Tag</th>
                    <th style="padding: 4px">Anfragen</th>
                    <th style="padding: 4px">429</th>
                    <th style="padding: 4px">4xx</th>
                    <th style="padding: 4px">5xx</th>
                    <th style="padding: 4px">Ø Dauer</th>
                    <th style="padding: 4px">max. Dauer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="day in usage[slotProps.data.id]"
                    :key="day.day"
                    :style="isOutlier(slotProps.data.id, day) ? 'background: #fff3cd' : ''"
                  >
                    <td style="padding: 4px">{{ formatDate(day.day) }}</td>
                    <td style="padding: 4px">{{ day.requests }}</td>
                    <td style="padding: 4px">{{ day.rateLimitedRequests }}</td>
                    <td style="padding: 4px">{{ day.clientErrors }}</td>
                    <td style="padding: 4px">{{ day.serverErrors }}</td>
                    <td style="padding: 4px">{{ day.averageDurationMillis }} ms</td>
                    <td style="padding: 4px">{{ day.maxDurationMillis }} ms</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </DataTable>

      <Dialog
        v-model:visible="editDialog"
        :header="editedKey.id ? 'Schlüssel bearbeiten' : 'Neuen Schlüssel anlegen'"
        :modal="true"
        :style="{ width: '520px' }"
      >
        <div style="display: flex; flex-direction: column; gap: 10px">
          <div>
            <label>Name *</label>
            <InputText v-model="editedKey.name" style="width: 100%" />
          </div>
          <div>
            <label>Kontaktperson</label>
            <InputText v-model="editedKey.ownerName" style="width: 100%" />
          </div>
          <div>
            <label>E-Mail</label>
            <InputText v-model="editedKey.ownerMail" style="width: 100%" />
          </div>
          <div style="display: flex; gap: 10px">
            <div style="flex: 1">
              <label>Anfragen pro Minute</label>
              <InputNumber
                v-model="editedKey.requestsPerMinute"
                :min="1"
                :max="10000"
                style="width: 100%"
                :placeholder="String(defaultRequestsPerMinute)"
              />
            </div>
            <div style="flex: 1">
              <label>Anfragen pro Tag</label>
              <InputNumber
                v-model="editedKey.requestsPerDay"
                :min="1"
                :max="1000000"
                style="width: 100%"
                :placeholder="String(defaultRequestsPerDay)"
              />
            </div>
          </div>
          <div>
            <label>Gültig bis</label>
            <Calendar
              v-model="editedKey.expiresAt"
              dateFormat="dd.mm.yy"
              showButtonBar
              style="width: 100%"
            />
            <div style="font-size: 0.85em; color: #6c757d">
              Leer lassen für unbefristete Gültigkeit.
            </div>
          </div>
          <div>
            <label>Notizen</label>
            <Textarea v-model="editedKey.notes" rows="3" style="width: 100%" />
          </div>
        </div>
        <template #footer>
          <Button label="Abbrechen" severity="secondary" @click="editDialog = false" />
          <Button
            :label="editedKey.id ? 'Speichern' : 'Anlegen'"
            :disabled="busy || !editedKey.name"
            @click="saveKey"
          />
        </template>
      </Dialog>

      <Dialog
        v-model:visible="plainKeyDialog"
        header="Schlüssel wurde erzeugt"
        :modal="true"
        :closable="false"
        :style="{ width: '620px' }"
      >
        <Message severity="warn" :closable="false">
          Dieser Schlüssel wird nur jetzt angezeigt und kann später nicht erneut
          abgerufen werden. Gib ihn dem Empfänger über einen sicheren Weg weiter.
        </Message>
        <div style="display: flex; gap: 6px; margin-top: 12px">
          <InputText :value="plainKey" readonly style="flex: 1; font-family: monospace" />
          <Button icon="pi pi-copy" :label="copied ? 'Kopiert' : 'Kopieren'" @click="copyPlainKey" />
        </div>
        <template #footer>
          <Button label="Schließen" @click="closePlainKeyDialog" />
        </template>
      </Dialog>

      <Dialog
        v-model:visible="blockDialog"
        header="Schlüssel sperren"
        :modal="true"
        :style="{ width: '480px' }"
      >
        <div>
          Der Schlüssel <strong>{{ selectedKey ? selectedKey.name : "" }}</strong> wird
          sofort abgelehnt. Die Sperre wirkt ohne Verzögerung.
        </div>
        <div style="margin-top: 10px">
          <label>Grund (nur intern sichtbar)</label>
          <InputText v-model="blockReason" style="width: 100%" />
        </div>
        <template #footer>
          <Button label="Abbrechen" severity="secondary" @click="blockDialog = false" />
          <Button label="Sperren" severity="warning" :disabled="busy" @click="blockKey" />
        </template>
      </Dialog>

      <Dialog
        v-model:visible="deleteDialog"
        header="Schlüssel löschen"
        :modal="true"
        :style="{ width: '480px' }"
      >
        <div>
          Der Schlüssel <strong>{{ selectedKey ? selectedKey.name : "" }}</strong> und
          seine Nutzungsdaten werden endgültig gelöscht. Zum vorübergehenden Entziehen
          des Zugriffs ist Sperren die bessere Wahl.
        </div>
        <template #footer>
          <Button label="Abbrechen" severity="secondary" @click="deleteDialog = false" />
          <Button label="Löschen" severity="danger" :disabled="busy" @click="deleteKey" />
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Textarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import Message from "primevue/message";
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import FailureHandler from "../../services/ServerConnection/FailureHandler";
import { useMenuService } from "../../services/MenuService/UseMenuService";

const USAGE_DAYS = 30;

export default {
  name: "ApiKeyManagementView",
  components: {
    DataTable,
    Column,
    Button,
    Dialog,
    InputText,
    InputNumber,
    Textarea,
    Calendar,
    Message,
  },
  data() {
    return {
      apiKeys: [],
      expandedRows: [],
      usage: {},
      loadingUsage: {},
      loading: false,
      busy: false,
      error: "",
      editDialog: false,
      editedKey: this.emptyKey(),
      plainKeyDialog: false,
      plainKey: "",
      copied: false,
      blockDialog: false,
      blockReason: "",
      deleteDialog: false,
      selectedKey: null,
      defaultRequestsPerMinute: 60,
      defaultRequestsPerDay: 5000,
    };
  },
  mounted() {
    const menuService = useMenuService();
    menuService.setMainMenuItemNames(["home"]);
    this.loadKeys();
  },
  methods: {
    emptyKey() {
      return {
        id: null,
        name: "",
        ownerName: "",
        ownerMail: "",
        notes: "",
        requestsPerMinute: null,
        requestsPerDay: null,
        expiresAt: null,
      };
    },
    loadKeys() {
      this.loading = true;
      this.error = "";
      ServerConnection.get("admin/apikeys")
        .then((response) => {
          this.apiKeys = response.data;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    onRowExpand(event) {
      this.loadUsage(event.data.id);
    },
    loadUsage(id) {
      this.loadingUsage = { ...this.loadingUsage, [id]: true };
      ServerConnection.get("admin/apikeys/" + id + "/usage?days=" + USAGE_DAYS)
        .then((response) => {
          this.usage = { ...this.usage, [id]: response.data.slice().reverse() };
          this.loadingUsage = { ...this.loadingUsage, [id]: false };
        })
        .catch((error) => {
          this.loadingUsage = { ...this.loadingUsage, [id]: false };
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    openCreateDialog() {
      this.editedKey = this.emptyKey();
      this.editDialog = true;
    },
    openEditDialog(apiKey) {
      this.editedKey = {
        id: apiKey.id,
        name: apiKey.name,
        ownerName: apiKey.ownerName,
        ownerMail: apiKey.ownerMail,
        notes: apiKey.notes,
        requestsPerMinute: apiKey.requestsPerMinute,
        requestsPerDay: apiKey.requestsPerDay,
        expiresAt: apiKey.expiresAt ? new Date(apiKey.expiresAt) : null,
      };
      this.editDialog = true;
    },
    saveKey() {
      this.busy = true;
      this.error = "";
      var body = {
        name: this.editedKey.name,
        ownerName: this.editedKey.ownerName,
        ownerMail: this.editedKey.ownerMail,
        notes: this.editedKey.notes,
        requestsPerMinute: this.editedKey.requestsPerMinute,
        requestsPerDay: this.editedKey.requestsPerDay,
        expiresAt: this.toIsoDateTime(this.editedKey.expiresAt),
      };
      var request = this.editedKey.id
        ? ServerConnection.put("admin/apikeys/" + this.editedKey.id, body)
        : ServerConnection.post("admin/apikeys", body);
      request
        .then((response) => {
          this.busy = false;
          this.editDialog = false;
          if (!this.editedKey.id) {
            this.plainKey = response.data.plainKey;
            this.copied = false;
            this.plainKeyDialog = true;
          }
          this.loadKeys();
        })
        .catch((error) => {
          this.busy = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    copyPlainKey() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.plainKey).then(() => {
          this.copied = true;
        });
      }
    },
    closePlainKeyDialog() {
      this.plainKey = "";
      this.copied = false;
      this.plainKeyDialog = false;
    },
    openBlockDialog(apiKey) {
      this.selectedKey = apiKey;
      this.blockReason = "";
      this.blockDialog = true;
    },
    blockKey() {
      this.busy = true;
      ServerConnection.post("admin/apikeys/" + this.selectedKey.id + "/block", {
        reason: this.blockReason,
      })
        .then(() => {
          this.busy = false;
          this.blockDialog = false;
          this.loadKeys();
        })
        .catch((error) => {
          this.busy = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    unblockKey(apiKey) {
      this.busy = true;
      ServerConnection.post("admin/apikeys/" + apiKey.id + "/unblock", {})
        .then(() => {
          this.busy = false;
          this.loadKeys();
        })
        .catch((error) => {
          this.busy = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    openDeleteDialog(apiKey) {
      this.selectedKey = apiKey;
      this.deleteDialog = true;
    },
    deleteKey() {
      this.busy = true;
      ServerConnection.delete("admin/apikeys/" + this.selectedKey.id)
        .then(() => {
          this.busy = false;
          this.deleteDialog = false;
          this.loadKeys();
        })
        .catch((error) => {
          this.busy = false;
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    quotaText(apiKey) {
      var perMinute = apiKey.requestsPerMinute || this.defaultRequestsPerMinute;
      var perDay = apiKey.requestsPerDay || this.defaultRequestsPerDay;
      return perMinute + "/min · " + perDay + "/Tag";
    },
    isExpired(apiKey) {
      return apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date();
    },
    totalRequests(id) {
      return (this.usage[id] || []).reduce((sum, day) => sum + day.requests, 0);
    },
    totalRateLimited(id) {
      return (this.usage[id] || []).reduce((sum, day) => sum + day.rateLimitedRequests, 0);
    },
    isOutlier(id, day) {
      var days = this.usage[id] || [];
      if (days.length < 5) {
        return false;
      }
      var values = days.map((entry) => entry.requests).sort((a, b) => a - b);
      var median = values[Math.floor(values.length / 2)];
      return median > 0 && day.requests > 3 * median;
    },
    toIsoDateTime(value) {
      if (!value) {
        return null;
      }
      var date = value instanceof Date ? value : new Date(value);
      var pad = (number) => String(number).padStart(2, "0");
      return (
        date.getFullYear() +
        "-" +
        pad(date.getMonth() + 1) +
        "-" +
        pad(date.getDate()) +
        "T23:59:59"
      );
    },
    formatDate(value) {
      if (!value) {
        return "";
      }
      var parts = String(value).split("-");
      if (parts.length !== 3) {
        return value;
      }
      return parts[2] + "." + parts[1] + "." + parts[0];
    },
    formatDateTime(value) {
      if (!value) {
        return "";
      }
      var text = String(value).replace("T", " ");
      var parts = text.split(" ");
      var dateParts = parts[0].split("-");
      if (dateParts.length !== 3) {
        return text;
      }
      var time = parts.length > 1 ? " " + parts[1].substring(0, 5) : "";
      return dateParts[2] + "." + dateParts[1] + "." + dateParts[0] + time;
    },
  },
};
</script>
